import React, { useState } from 'react';
import { motion } from "framer-motion";
import { blogPosts } from "../data/blog-posts";
import BlogModal from '../components/sections/BlogModal';

// Interface de base du blog post (depuis blog-posts.ts)
interface BlogPost {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  tags: string[];
  content?: string;
  mermaid?: string | null;
  language?: string;
  image?: string;
}

// Type étendu avec propriétés calculées pour l'affichage
type EnhancedBlogPost = BlogPost & {
  slug?: string;
  lang?: string;
};

/**
 * Génère un slug URL-friendly à partir d'un titre ou identifiant
 */
function generateSlug(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/['']/g, '') // Supprimer les apostrophes
    .replace(/[?]/g, '') // Supprimer les points d'interrogation
    .replace(/[.]/g, '') // Supprimer les points
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/[^\w-]/g, '') // Supprimer tous les caractères non-mot
    .replace(/--+/g, '-'); // Remplacer les multiples tirets par un seul
}

/**
 * Extrait un court résumé HTML du contenu de l'article
 */
function getExcerpt(post: BlogPost): string {
  // Utiliser le résumé s'il existe et est déjà du HTML (ou s'il doit être converti)
  if (post.summary) {
    // Si le résumé est déjà du HTML (supposons), on le retourne directement
    return post.summary; 
    // Si le résumé est du Markdown, il faudrait le convertir ici
    // return marked.parse(post.summary);
  }
  
  if (post.content) {
    // Le contenu est déjà du HTML généré par generate-articles.js
    // Extraire le contenu des premières balises <p>
    const firstParagraphMatch = post.content.match(/<p>(.*?)<\/p>/i);
    let excerpt = firstParagraphMatch ? `<p>${firstParagraphMatch[1]}</p>` : '';

    // Limiter la longueur (approximativement, car on travaille avec HTML)
    const maxLength = 200;
    if (excerpt.length > maxLength) {
      // Couper proprement avant la fin d'une balise si possible
      excerpt = excerpt.substring(0, maxLength);
      // Fermer les balises ouvertes (simpliste, pourrait être amélioré)
      if (excerpt.lastIndexOf('<') > excerpt.lastIndexOf('>')) {
        excerpt = excerpt.substring(0, excerpt.lastIndexOf('<'));
      }
      excerpt += '...';
      // S'assurer que le paragraphe est fermé
      if (excerpt.startsWith('<p>') && !excerpt.endsWith('</p>')) {
        excerpt += '</p>';
      }
    } else if (excerpt.length === 0 && post.content.length > 0) {
        // Si aucune balise <p> trouvée, prendre le début du contenu brut
        excerpt = post.content.substring(0, maxLength) + (post.content.length > maxLength ? '...' : '');
    }
    
    return excerpt;
  }
  
  return '';
}

/**
 * Convertir une date française en format ISO
 */
function parseFrenchDate(dateString: string): Date {
  const months: { [key: string]: number } = {
    'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
    'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
    'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
  };
  
  // Format: "13 septembre 2025"
  const parts = dateString.toLowerCase().split(' ');
  if (parts.length === 3) {
    const day = parseInt(parts[0]);
    const month = months[parts[1]];
    const year = parseInt(parts[2]);
    
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }
  
  // Si ce n'est pas un format français, essayer le format ISO
  return new Date(dateString);
}

/**
 * Formate une date en format localisé
 */
function formatDate(dateString: string): string {
  try {
    const date = parseFrenchDate(dateString);
    
    if (isNaN(date.getTime())) {
      return dateString; // Si la date est invalide, retourner la chaîne originale
    }
    
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString; // En cas d'erreur, renvoyer la chaîne originale
  }
}

const Blog = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('fr');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  
  // Trier les articles par date (plus récent en premier)
  const sortedPosts = [...blogPosts].sort((a, b) => {
    const dateA = parseFrenchDate(a.date);
    const dateB = parseFrenchDate(b.date);
    
    // Vérifier que les dates sont valides
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;
    
    // Trier du plus récent au plus ancien
    return dateB.getTime() - dateA.getTime();
  });

  // Filtrer par langue
  const filteredPosts = sortedPosts.filter((post) => {
    const postLang = post.language || (post as any).lang || "fr";
    return postLang === selectedLanguage;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen py-8"
      style={{
        background: 'url(/images/meet-background.png) center/cover no-repeat fixed'
      }}
    >
      <div className="container mx-auto px-4">
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-3 text-gray-900 drop-shadow-sm">
          Blog
        </h1>
        <p className="text-sm md:text-base mx-auto max-w-4xl text-gray-800">
          Actualités tech, innovations IA et insights exclusifs
        </p>
      </div>
      
      {/* Sélecteur de langue */}
      <div className="flex justify-center gap-2 mb-8">
        <button
          className={`px-4 py-2 rounded-full transition-all ${
            selectedLanguage === "fr"
              ? "bg-blue-500 text-white shadow-md"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setSelectedLanguage("fr")}
        >
          FR
        </button>
        <button
          className={`px-4 py-2 rounded-full transition-all ${
            selectedLanguage === "en"
              ? "bg-blue-500 text-white shadow-md"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setSelectedLanguage("en")}
        >
          EN
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
        {filteredPosts.map((post) => {
          // Créer un objet étendu avec les propriétés calculées
          const enhancedPost: EnhancedBlogPost = {
            ...post,
            slug: generateSlug(post.id),
            lang: post.language || "fr"
          };
          
          return (
            <div
              key={enhancedPost.id}
              onClick={() => setSelectedPost(post)}
              className="block transition-transform hover:-translate-y-2 duration-300 cursor-pointer"
            >
              <article
                className="h-full rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all"
                style={{
                  /* Verre dépoli grisâtre */
                  background: 'rgba(128, 128, 128, 0.12)',
                  backdropFilter: 'blur(10px) saturate(150%)',
                  WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                  border: '1px solid rgba(200, 200, 200, 0.3)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 2px 10px rgba(255, 255, 255, 0.2)',
                  willChange: 'backdrop-filter',
                }}
              >
                {enhancedPost.image && (
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={enhancedPost.image}
                      alt={enhancedPost.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                )}
                <div className="p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-black text-xs font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        background: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {enhancedPost.category}
                    </span>
                  </div>
                  <h2 className="text-base md:text-lg font-bold mb-2 text-black drop-shadow-sm line-clamp-2">{enhancedPost.title}</h2>
                  <div
                     className="text-[#666666] mb-2 line-clamp-2 hidden"
                     style={{ lineHeight: '1.4', fontSize: '0.75rem' }}
                     dangerouslySetInnerHTML={{ __html: getExcerpt(enhancedPost) }}
                  />
                  <time className="text-black text-xs block mt-2 pt-2 border-t border-gray-300">
                    {formatDate(enhancedPost.date)}
                  </time>
                </div>
              </article>
            </div>
          );
        })}
      </div>

      {/* Blog Modal */}
      <BlogModal
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
      />
      
      {filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-white text-xl">Aucun article ne correspond à vos critères de recherche.</p>
        </div>
      )}
      </div>
    </motion.div>
  );
};

export default Blog; 